import React from "react";
import { Card } from "@/components/ui/card";
import { InfoIcon } from "lucide-react";

import { calculateOneTimeContractAmounts } from "@/service/weexpertService";
import { useQuery } from "react-query";

// Custom hook for contract calculations

export const useContractCalculations = (
  priceNetto,
  copyrightType,
  counterpartyId,
  currency = "PLN"
) => {
  return useQuery({
    queryKey: [
      "contractCalculations",
      priceNetto,
      copyrightType,
      counterpartyId,
      currency,
    ],
    queryFn: async () => {
      const response = await calculateOneTimeContractAmounts({
        priceNetto: parseFloat(priceNetto),
        copyrightType: copyrightType,
        counterpartyId: counterpartyId,
        currency: currency,
      });
      return response.data.data; // Extract the calculation data from the API response
    },
    enabled: !!(
      priceNetto &&
      !isNaN(parseFloat(priceNetto)) &&
      parseFloat(priceNetto) > 0 &&
      copyrightType &&
      counterpartyId
    ),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
    onError: (error) => {
      console.error("Calculation error:", error);
    },
  });
};

const CalculationBlock = ({
  priceNetto,
  currency,
  copyrightType,
  selectedCounterparty,
  counterpartyOrigin,
  counterpartyType,
  t,
}) => {
  const selectedCurrency =
    typeof currency === "object" ? currency?.value : currency || "PLN";

  const counterpartyId = selectedCounterparty?.id;

  const {
    data: calculations,
    isLoading: calculationsLoading,
    error: calculationsError,
  } = useContractCalculations(
    priceNetto,
    copyrightType?.value || copyrightType,
    counterpartyId,
    selectedCurrency
  );
  const contractorCalculation = calculations?.contractor || {};
  const counterpartyCalculation = calculations?.counterparty || {};
  const showLoadingOrError = calculationsLoading || calculationsError;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Contractor Calculation Block */}
      <Card className="p-6 bg-green-50">
        <h3 className="font-medium mb-4 text-green-800">
          {t("contracts:contractor_calculation")}
        </h3>
        {showLoadingOrError ? (
          <div className="space-y-2 text-sm">
            {calculationsLoading && (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-800"></div>
                <span className="ml-2 text-green-700">
                  {t("contracts:calculating")}
                </span>
              </div>
            )}
            {calculationsError && (
              <div className="flex items-center justify-center py-8 text-red-600">
                <InfoIcon className="w-5 h-5 mr-2" />
                <span className="text-sm">
                  {t("contracts:calculation_error")}
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-2 text-[12px]">
            <div className="flex justify-between">
              <span>{t("contracts:net_amount")}</span>
              <span className="font-medium">
                {contractorCalculation?.netto?.toFixed(2) || "0.00"}{" "}
                {selectedCurrency}
              </span>
            </div>
            <div className="flex justify-between">
              <span>{t("contracts:fee_deduction")}</span>
              <span>
                -{contractorCalculation?.fee?.toFixed(2) || "0.00"}{" "}
                {selectedCurrency}
              </span>
            </div>
            <div className="flex justify-between">
              <span>{t("contracts:calculation_base")}</span>
              <span className="font-medium">
                {contractorCalculation?.baseForContractor?.toFixed(2) || "0.00"}{" "}
                {selectedCurrency}
              </span>
            </div>
            <div className="flex justify-between">
              <span>
                {t("contracts:tax_deductible_costs")} (
                {contractorCalculation?.taxDeductibleRate || 0}%)
              </span>
              <span>
                -
                {contractorCalculation?.taxDeductibleAmount?.toFixed(2) ||
                  "0.00"}{" "}
                {selectedCurrency}
              </span>
            </div>
            <div className="flex justify-between">
              <span>{t("contracts:taxation_base")}</span>
              <span>
                {contractorCalculation?.taxationBase?.toFixed(2) || "0.00"}{" "}
                {selectedCurrency}
              </span>
            </div>
            <div className="flex justify-between">
              <span>{t("contracts:pit_tax")}</span>
              <span>
                -{contractorCalculation?.pitAmount?.toFixed(2) || "0.00"} {selectedCurrency}
              </span>
            </div>
            {calculations?.contractor?.differenceCurrencyInPit && (
              <div className="flex justify-between text-sm text-gray-600">
                <span>{t("contracts:currency_difference")}</span>
                <span>
                  {calculations.contractor.differenceCurrencyInPit?.toFixed(2)} {selectedCurrency}
                </span>
              </div>
            )}
            {calculations?.contractor?.bankFee&& (
              <div className="flex justify-between text-sm text-gray-600">
                <span>{t("contracts:currency_difference")}</span>
                <span>
                  {calculations.contractor.bankFee?.toFixed(2)} {selectedCurrency}
                </span>
              </div>
            )}
            <div className="border-t pt-2 flex justify-between font-semibold text-green-800 text-sm">
              <span>{t("contracts:to_payout")}</span>
              <span>
                {contractorCalculation?.toPay?.toFixed(2) || "0.00"}{" "}
                {selectedCurrency}
              </span>
            </div>
          </div>
        )}
      </Card>

      {/* Counterparty Calculation Block */}
      <Card className="p-6 bg-blue-50">
        <h3 className="font-medium mb-4 text-blue-800">
          {t("contracts:counterparty_calculation")}
        </h3>
        {showLoadingOrError ? (
          <div className="space-y-2 text-sm">
            {calculationsLoading && (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-800"></div>
                <span className="ml-2 text-blue-700">
                  {t("contracts:calculating")}
                </span>
              </div>
            )}
            {calculationsError && (
              <div className="flex items-center justify-center py-8 text-red-600">
                <InfoIcon className="w-5 h-5 mr-2" />
                <span className="text-sm">
                  {t("contracts:calculation_error")}
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-2 text-[12px]">
            <div className="flex justify-between">
              <span>{t("contracts:net_amount")}</span>
              <span className="font-medium">
                {counterpartyCalculation?.netto?.toFixed(2) || "0.00"}{" "}
                {selectedCurrency}
              </span>
            </div>
            <div className="flex justify-between">
              <span>
                VAT ({(counterpartyCalculation?.vatRate * 100 || 0).toFixed(0)}
                %)
              </span>
              <span className="font-medium">
                {counterpartyCalculation?.vat?.toFixed(2) || "0.00"}{" "}
                {selectedCurrency}
              </span>
            </div>
            {counterpartyCalculation?.vatRate === 0 && (
              <div className="text-xs text-blue-600 italic">
                {t("contracts:vat_not_applicable_non_pl_company")}
              </div>
            )}
            <div className="border-t pt-2 flex justify-between font-semibold text-blue-800 text-sm">
              <span>{t("contracts:gross_to_pay")}</span>
              <span>
                {counterpartyCalculation?.brutto?.toFixed(2) || "0.00"}{" "}
                {selectedCurrency}
              </span>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default CalculationBlock;
